import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader, ChevronLeft, Tag, Plus, CreditCard as Edit2, Trash2, AlertCircle, FolderOpen } from 'lucide-react';
import { supabase } from '../lib/supabase';
import DeleteConfirmModal from './DeleteConfirmModal';

interface Category {
  id: string;
  name: string;
  created_at: string;
  collectible_count?: number;
}

function AdminCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    const checkAdminAndFetchCategories = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('Not authenticated');
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single();

        if (!profile?.is_admin) {
          throw new Error('Not authorized');
        }

        setIsAdmin(true);
        await fetchCategories();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load categories');
      } finally {
        setLoading(false);
      }
    };

    checkAdminAndFetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select(`
          id,
          name,
          created_at
        `)
        .order('name');

      if (categoriesError) throw categoriesError;

      // Get collectible count for each category
      const categoriesWithCount = await Promise.all(
        (categoriesData || []).map(async (category) => {
          const { count } = await supabase
            .from('collectibles')
            .select('*', { count: 'exact', head: true })
            .eq('category_id', category.id);
          return { ...category, collectible_count: count || 0 };
        })
      );

      setCategories(categoriesWithCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    }
  };

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoryName.trim()) return;

    setSaving(true);
    try {
      const { error: insertError } = await supabase
        .from('categories')
        .insert({ name: categoryName.trim() });

      if (insertError) {
        if (insertError.code === '23505') {
          throw new Error('A category with this name already exists');
        }
        throw insertError;
      }

      setCategoryName('');
      setShowAddForm(false);
      await fetchCategories();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add category');
    } finally {
      setSaving(false);
    }
  };

  const handleEditCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoryName.trim() || !editingCategory) return;

    setSaving(true);
    try {
      const { error: updateError } = await supabase
        .from('categories')
        .update({ name: categoryName.trim() })
        .eq('id', editingCategory.id);

      if (updateError) {
        if (updateError.code === '23505') {
          throw new Error('A category with this name already exists');
        }
        throw updateError;
      }

      setCategoryName('');
      setEditingCategory(null);
      setIsEditing(false);
      await fetchCategories();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;

    try {
      // Check if category has collectibles
      if (selectedCategory.collectible_count && selectedCategory.collectible_count > 0) {
        alert(`Cannot delete category "${selectedCategory.name}" because it has ${selectedCategory.collectible_count} collectible(s) assigned. Please reassign or delete those collectibles first.`);
        setShowDeleteModal(false);
        setSelectedCategory(null);
        return;
      }

      const { error: deleteError } = await supabase
        .from('categories')
        .delete()
        .eq('id', selectedCategory.id);

      if (deleteError) throw deleteError;

      setCategories(prev => prev.filter(c => c.id !== selectedCategory.id));
      setShowDeleteModal(false);
      setSelectedCategory(null);
    } catch (err) {
      console.error('Error deleting category:', err);
      alert('Failed to delete category. Please try again.');
    }
  };

  const startEditing = (category: Category) => {
    setEditingCategory(category);
    setCategoryName(category.name);
    setIsEditing(true);
    setShowAddForm(false);
  };

  const cancelEditing = () => {
    setEditingCategory(null);
    setCategoryName('');
    setIsEditing(false);
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to view this page.</p>
          <Link to="/" className="text-primary-600 hover:text-primary-800 font-medium">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Manage Categories - Admin</title>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft className="h-5 w-5" />
              Back to Home
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">Manage Categories</h1>
          </div>
          <div className="bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-sm font-medium">
            {categories.length} Categories
          </div>
        </div>

        {/* Add/Edit Form */}
        {(showAddForm || isEditing) && (
          <div className="mb-6 bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {isEditing ? 'Edit Category' : 'Add New Category'}
            </h3>
            <form onSubmit={isEditing ? handleEditCategory : handleAddCategory} className="flex gap-4">
              <input
                type="text"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Category name"
                className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                autoFocus
              />
              <button
                type="submit"
                disabled={saving || !categoryName.trim()}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    {isEditing ? (
                      <>
                        <Edit2 className="h-4 w-4" />
                        Save
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4" />
                        Add
                      </>
                    )}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  cancelEditing();
                  setShowAddForm(false);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </form>
          </div>
        )}

        {/* Add Button (when form not shown) */}
        {!showAddForm && !isEditing && (
          <div className="mb-6">
            <button
              onClick={() => {
                setShowAddForm(true);
                cancelEditing();
              }}
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Add Category
            </button>
          </div>
        )}

        {/* Categories List */}
        {categories.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
            <FolderOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Categories Yet</h3>
            <p className="text-gray-600">Add your first category to get started.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {categories.map((category) => (
              <div
                key={category.id}
                className="bg-white rounded-xl border border-gray-200 p-6"
              >
                {isEditing && editingCategory?.id === category.id ? (
                  <form onSubmit={handleEditCategory} className="flex gap-4">
                    <input
                      type="text"
                      value={categoryName}
                      onChange={(e) => setCategoryName(e.target.value)}
                      className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      autoFocus
                    />
                    <button
                      type="submit"
                      disabled={saving || !categoryName.trim()}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? <Loader className="h-4 w-4 animate-spin" /> : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEditing}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                  </form>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-primary-100 text-primary-600 p-3 rounded-lg">
                        <Tag className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{category.name}</h3>
                        <p className="text-sm text-gray-500">
                          {category.collectible_count} collectible{category.collectible_count !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => startEditing(category)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
                      >
                        <Edit2 className="h-4 w-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          setSelectedCategory(category);
                          setShowDeleteModal(true);
                        }}
                        className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center gap-2"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedCategory(null);
        }}
        onConfirm={handleDelete}
        title={selectedCategory?.name || ''}
      />
    </>
  );
}

export default AdminCategories;
